import { type Ref, onMounted } from 'vue'

import { useDebounceFn, useEventListener } from '@vueuse/core'

import type { ImagesSequence } from './ImagesSequence.vue'

interface CanvasSizes {
  width: number
  height: number
}

/**
 *
 * Rendering functions
 */

export function scaleToFit(params: {
  image: HTMLImageElement
  canvasSizes: CanvasSizes
  devicePixelR: number
}): {
  x: number
  y: number
  scale: number
} {
  const { image, canvasSizes, devicePixelR } = params

  const imageWidth = image.naturalWidth
  const imageHeight = image.naturalHeight

  const scale = Math.max(
    canvasSizes.width / imageWidth,
    canvasSizes.height / imageHeight,
  )

  const x = (canvasSizes.width / 2 - (imageWidth / 2) * scale) / devicePixelR
  const y = (canvasSizes.height / 2 - (imageHeight / 2) * scale) / devicePixelR

  return {
    x,
    y,
    scale: scale / devicePixelR,
  }
}

export function drawImageToCanvas(params: {
  alpha: number
  image: HTMLImageElement
  canvasSizes: CanvasSizes
  devicePixelR: number
  ctx: CanvasRenderingContext2D
}): void {
  const { alpha, image, canvasSizes, devicePixelR, ctx } = params

  const drawValues = scaleToFit({ image, canvasSizes, devicePixelR })

  ctx.clearRect(
    drawValues.x,
    drawValues.y,
    image.naturalWidth * drawValues.scale,
    image.naturalHeight * drawValues.scale,
  )
  ctx.globalAlpha = alpha
  ctx.drawImage(
    image,
    drawValues.x,
    drawValues.y,
    image.naturalWidth * drawValues.scale,
    image.naturalHeight * drawValues.scale,
  )
}

/**
 *
 * Zoom functions
 */

export function drawMagnifyingGlass(params: {
  canvasSizes: CanvasSizes
  ctx: CanvasRenderingContext2D
  devicePixelR: number
  image: HTMLImageElement
  xPos: number
  yPos: number
  zoomIntensity: number
  zoomSize: number
}) {
  const {
    canvasSizes,
    ctx,
    devicePixelR,
    image,
    xPos,
    yPos,
    zoomIntensity,
    zoomSize,
  } = params

  const drawValues = scaleToFit({ image, canvasSizes, devicePixelR })

  ctx.clearRect(
    drawValues.x,
    drawValues.y,
    image.naturalWidth * drawValues.scale,
    image.naturalHeight * drawValues.scale,
  )

  ctx.drawImage(
    image,
    drawValues.x,
    drawValues.y,
    image.naturalWidth * drawValues.scale,
    image.naturalHeight * drawValues.scale,
  )
  ctx.save()

  // create clip
  ctx.beginPath()
  ctx.arc(xPos, yPos, zoomSize, 0, Math.PI * 2, true)
  ctx.closePath()
  ctx.clip()

  // Erase zoom zone
  ctx.clearRect(xPos - zoomSize, yPos - zoomSize, zoomSize * 2, zoomSize * 2)
  ctx.restore()
  ctx.save()

  ctx.beginPath()
  ctx.arc(xPos, yPos, zoomSize, 0, Math.PI * 2, true)
  ctx.closePath()
  ctx.clip()

  // Zoom image offset
  const zoomedWidth = image.naturalWidth * drawValues.scale * zoomIntensity
  const zoomedHeight = image.naturalHeight * drawValues.scale * zoomIntensity
  const zoomedImageOffsetX = (xPos - drawValues.x) * (zoomIntensity - 1)
  const zoomedImageOffsetY = (yPos - drawValues.y) * (zoomIntensity - 1)

  // draw image
  ctx.drawImage(
    image,
    drawValues.x - zoomedImageOffsetX,
    drawValues.y - zoomedImageOffsetY,
    zoomedWidth,
    zoomedHeight,
  )

  // Restore the state
  ctx.restore()

  // Draw the outline of the circle
  ctx.beginPath()
  ctx.arc(xPos, yPos, zoomSize, 0, Math.PI * 2, true)
  ctx.strokeStyle = 'black'
  ctx.lineWidth = 2
  ctx.stroke()
  ctx.closePath()
}

/**
 *
 * Loading functions
 */

export function loadImage(path: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      resolve(image)
    }

    const msg = `Could not load image at ${path}`
    image.onerror = () => {
      reject(new Error(msg))
    }
    image.src = path
  })
}

export async function loadImagesWithInterval(params: {
  count: number
  format: string
  staticPath: string
  interval: number
  activeSequence: ImagesSequence
}): Promise<{ images: HTMLImageElement[]; alreadyLoaded: Set<number> }> {
  const { count, format, staticPath, interval, activeSequence } = params
  const promises = []

  for (let i = 0; i <= count; i += interval) {
    if (activeSequence.alreadyLoaded.has(i)) {
      continue
    }

    const formattedNumber = String(i).padStart(3, '0')
    const path = `${staticPath + formattedNumber}.${format}`
    const promise = loadImage(path)
    promises.push(promise)
    activeSequence.alreadyLoaded.add(i)
  }

  const results = await Promise.allSettled(promises)
  const images = results
    .filter(
      (result): result is PromiseFulfilledResult<HTMLImageElement> =>
        result.status === 'fulfilled',
    )
    .map((result) => result.value)

  return { images, alreadyLoaded: activeSequence.alreadyLoaded }
}

/**
 * Updates the canvas position in the document on every scroll and resize
 * @param canvasRef
 * @returns canvasPos a non-reactive object of the canvas top-right edge position (x,y) within the document
 */
export function useCanvasPosition(canvasRef: Ref<HTMLCanvasElement | null>) {
  // canvasPos is the (x,y) point of the top-right edge of the canvas within the document
  // canvasPos is not reactive as it's not used in the template or used in a watcher
  const canvasPos: { x: number; y: number } = { x: 0, y: 0 }

  function updateCanvasPosition() {
    const canvasBounds = canvasRef.value?.getBoundingClientRect()
    if (!canvasBounds) return

    canvasPos.y = canvasBounds.top + window.scrollY
    canvasPos.x = canvasBounds.left
  }

  const debouncedUpdateCanvasPosition = useDebounceFn(updateCanvasPosition, 100)

  useEventListener(document, 'scroll', debouncedUpdateCanvasPosition)
  useEventListener(window, 'resize', debouncedUpdateCanvasPosition)

  onMounted(() => {
    // we add a delay before setting the initial position of the canvas to take into account elements showing up after hydration (e.g. a top banner)
    // the 2 seconds delay has been chosen rather arbitrarily but it fixes the issues we had (magnifying glass not being centered until the end of the debounce time from debouncedUpdateCanvasPosition)
    setTimeout(updateCanvasPosition, 2000)
  })

  return {
    updateCanvasPosition,
    canvasPos,
  }
}
